import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { z } from "zod";

import type { RouterInputs } from "@/lib/types";

type ReceiveAccessionInput = RouterInputs["accession"]["receive"];

// biome-ignore lint/suspicious/noExplicitAny: it's ok
type AnyZodObject = z.ZodObject<any, any>;

const receiveSchema = z.object({
  id: z.number(),
});

// biome-ignore lint/suspicious/noExplicitAny: breaks stuff
type ConvertFn = (values: any) => Partial<ReceiveAccessionInput>;

type ReceiveFormContextValues = {
  schema: AnyZodObject;
  mergeSchema: (schema: AnyZodObject) => void;
  registerConverter: (id: string, converter: ConvertFn) => void;
  convert: ConvertFn;
};

const ReceiveFormContext = createContext({} as ReceiveFormContextValues);

/**
 * This provides a form schema with methods to extend/modify that schema.
 * This allows for extending the "receive" workflow with new form fields,
 * such as shipper validation.
 */
export function ReceiveFormProvider({ children }: { children: ReactNode }) {
  const [schema, setSchema] = useState<AnyZodObject>(receiveSchema);
  const [converters, setConverters] = useState<
    { id: string; converter: ConvertFn }[]
  >([]);

  const mergeSchema = useCallback((schema: AnyZodObject) => {
    setSchema((s) => s.extend(schema.shape));
  }, []);

  const registerConverter: ReceiveFormContextValues["registerConverter"] =
    useCallback((id, converter) => {
      setConverters((converters) => {
        const existing = converters.find((c) => c.id === id);
        if (!existing) {
          return [...converters, { id, converter }];
        }
        return converters;
      });
    }, []);

  const convert = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: looks like we need this
    (values: any) => {
      return converters.reduce(
        (prev, curr) => {
          const converted = curr.converter(values);
          // biome-ignore lint/performance/noAccumulatingSpread: shrug
          return { ...prev, ...converted };
        },
        // start with accession ID
        { id: values.id },
      );
    },
    [converters],
  );

  return (
    <ReceiveFormContext.Provider
      value={{ schema, mergeSchema, registerConverter, convert }}
    >
      {children}
    </ReceiveFormContext.Provider>
  );
}

export function useReceiveForm() {
  const vals = useContext(ReceiveFormContext);
  if (!vals) {
    throw new Error("Must call useReceiveForm within ReceiveFormContext");
  }
  return vals;
}
